import { useEffect, useRef } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useFlags } from '@mint-lib/routing-context';

type Translate = (key: string) => string;

export const OAUTH_RESULTS = {
  success: 'success',
  flowNotFound: 'flow_not_found',
  providerError: 'provider_error',
  tokenExchangeFailed: 'token_exchange_failed',
  connectionNotFound: 'connection_not_found',
  platformMismatch: 'platform_mismatch',
  invalidProperty: 'invalid_property',
} as const;

// GA4 Mint 2.0 backend-owned OAuth results: only mapped while
// ga4BackendOwnedAuth is on; otherwise they fall back to the generic message,
// as before.
const getGA4BackendOwnedAuthMessage = (oauthResult: string, t: Translate) => {
  if (oauthResult === OAUTH_RESULTS.platformMismatch) {
    return t(
      'The connection session belongs to a different platform. Please try connecting your account again.',
    );
  }
  if (oauthResult === OAUTH_RESULTS.invalidProperty) {
    return t(
      'Your account was reconnected, but the previously selected property is no longer accessible. Please reconnect with an account that has access to it.',
    );
  }
  return undefined;
};

const getOAuthResultErrorMessage = (
  oauthResult: string,
  t: Translate,
  isGA4BackendOwnedAuth: boolean,
) => {
  const ga4Message = isGA4BackendOwnedAuth
    ? getGA4BackendOwnedAuthMessage(oauthResult, t)
    : undefined;
  if (ga4Message) return ga4Message;

  const oAuthResultErrorMessages = {
    [OAUTH_RESULTS.flowNotFound]: t(
      'The connection session expired or could not be found. Please try connecting your account again.',
    ),
    [OAUTH_RESULTS.providerError]: t(
      'The OAuth provider returned an error. Please try again or approve the requested access.',
    ),
    [OAUTH_RESULTS.tokenExchangeFailed]: t(
      'We could not complete the OAuth token exchange or save the token. Please try again.',
    ),
    [OAUTH_RESULTS.connectionNotFound]: t(
      'The connection you are trying to reconnect could not be found. Please refresh and try again.',
    ),
  } as const;

  const errorMessage =
    oAuthResultErrorMessages[
      oauthResult as keyof typeof oAuthResultErrorMessages
    ];

  return errorMessage ?? t('Failed to connect your account. Please try again.');
};

export type UseConnectionOAuthResultProps = {
  oauthResult: string;
  onError: () => void;
  onSuccess?: () => void;
};

const useConnectionOAuthResult = ({
  oauthResult,
  onError,
  onSuccess,
}: UseConnectionOAuthResultProps) => {
  const { t } = useTranslation('@mint-lib/connections-ui');
  const ff = useFlags('@mint/arm', ['ga4BackendOwnedAuth']);
  const isGA4BackendOwnedAuth = ff.ga4BackendOwnedAuth.enabled;
  const handledOAuthResult = useRef('');

  useEffect(() => {
    if (!oauthResult || handledOAuthResult.current === oauthResult) return;

    handledOAuthResult.current = oauthResult;

    if (oauthResult === OAUTH_RESULTS.success) {
      onSuccess?.();
      return;
    }

    // The token was refreshed but the connection still needs attention.
    const isWarning =
      isGA4BackendOwnedAuth && oauthResult === OAUTH_RESULTS.invalidProperty;
    enqueueSnackbar({
      variant: isWarning ? 'warning' : 'error',
      title: isWarning ? t('Action needed') : t('Error'),
      subTitle: getOAuthResultErrorMessage(
        oauthResult,
        t,
        isGA4BackendOwnedAuth,
      ),
    });

    onError();
  }, [oauthResult, onError, onSuccess, isGA4BackendOwnedAuth, t]);
};

export default useConnectionOAuthResult;
